<template>
  <div class="information-request-detail" data-testid="information-request-detail">
    <div
      v-if="isLoading"
      class="information-request-detail__loading pa-4"
      data-testid="information-request-detail__loading-skeleton"
    >
      <v-skeleton-loader type="heading" class="mb-4" />
      <v-skeleton-loader type="list-item-two-line" class="mb-2" />
      <v-skeleton-loader type="list-item-two-line" class="mb-2" />
      <v-skeleton-loader type="list-item-two-line" class="mb-4" />
      <v-skeleton-loader type="table-heading, table-row-divider@4" />
    </div>

    <v-card v-else class="all-width shadow-off">
      <v-card-text class="information-request-detail__body">
        <v-alert
          v-if="!request"
          type="warning"
          dense
          outlined
          data-testid="information-request-detail__not-found"
        >
          Request not found. It may still be loading or the link is invalid.
        </v-alert>

        <div v-else class="information-request-detail__layout">
          <RequestDetailHeader
            class="information-request-detail__header"
            :request="enrichedRequest"
            :serial-number="requestSerialNumber"
            :primary-actions="primaryActions"
            :overflow-actions="overflowActions"
            :overflow-open="overflowOpen"
            @back="navigateBack"
            @action="onHeaderAction"
            @overflow-toggle="overflowOpen = $event"
          />

          <div class="information-request-detail__content">
            <div class="information-request-detail__main">
              <RequestDetailBasicDetails
                :request="enrichedRequest"
                :location-name="locationName"
                :location-header="$store.state.LocationHeader || 'Location'"
              />

              <RequestDetailResponse
                :request="enrichedRequest"
                :can-submit-on-behalf="canSubmitOnBehalf"
                :can-edit="canEdit"
                :loading="responseLoading"
                @submit-response="onSubmitResponse"
                @download-file="onDownloadFile"
                @download-all="onDownloadAll"
              />

              <RequestDetailConversation
                :request="enrichedRequest"
                :project-id="pbcProjectId"
                :cycle-id="cycleId"
                :location-name="locationName"
                :request-serial="requestSerialNumber"
                :can-comment="canComment"
              />
            </div>

            <RequestDetailActivity
              :resource-id="requestId"
            />
          </div>
        </div>
      </v-card-text>
    </v-card>

    <AddEditRequestModal
      v-model="requestModalOpen"
      :loading="requestModalLoading"
      :request="editingRequest"
      :errors="requestFormErrors"
      :info-owner-list="infoOwnerList"
      :priority-list="priorityList"
      :category-list="categoryList"
      :location-list="locationList"
      :location-header="$store.state.LocationHeader || 'Location'"
      :min-date="$nova.formatDateISO(new Date())"
      :task-options="formattedRefList"
      :project-id="cycleId"
      @save="onSaveRequest"
      @clear-error="clearRequestFormError"
    />

    <AddEditOwnerModal
      v-model="ownerModalOpen"
      :loading="ownerModalLoading"
      :owner="editingOwner"
      @save="onSaveOwner"
    />

    <DeleteCancelConfirmModal
      v-model="confirmDialog.open"
      :variant="confirmDialog.variant"
      :loading="confirmDialog.loading"
      :custom-title="confirmDialog.title"
      :custom-message="confirmDialog.message"
      @confirm="onConfirmAction"
    />
  </div>
</template>

<script>
import './InformationRequestDetail.scss';
import { mapGetters } from 'vuex';
import {
  RequestDetailHeader,
  RequestDetailBasicDetails,
  RequestDetailResponse,
  RequestDetailActivity,
  RequestDetailConversation,
} from './components';
import { AddEditRequestModal, AddEditOwnerModal, DeleteCancelConfirmModal } from './modals';
import { getRequestActions, REQUEST_ACTION_IDS } from './utils/requestActions';
import { enrichRequestList } from './utils/requestList';
import {
  downloadAttachment,
  downloadAllAttachments,
} from './utils/requestDetail';
import {
  INFORMATION_MANAGER_PRIORITY_LIST,
} from '../../../utils/constants';
import {
  buildRequestUpdatePayload,
  resolveLocationName,
  resolveRequestSerialNumber,
  validateInformationRequest,
} from '../../../utils/informationManager';

export default {
  name: 'financialclose_project_informationrequest_detail',
  components: {
    RequestDetailHeader,
    RequestDetailBasicDetails,
    RequestDetailResponse,
    RequestDetailActivity,
    RequestDetailConversation,
    AddEditRequestModal,
    AddEditOwnerModal,
    DeleteCancelConfirmModal,
  },
  data() {
    return {
      right: 'financialclose_pbc_request',
      cycleId: this.$route.params.id,
      requestId: this.$route.params.requestId,
      overflowOpen: false,
      responseLoading: false,
      requestModalOpen: false,
      requestModalLoading: false,
      editingRequest: null,
      requestFormErrors: {},
      ownerModalOpen: false,
      ownerModalLoading: false,
      editingOwner: null,
      priorityList: [...INFORMATION_MANAGER_PRIORITY_LIST],
      confirmDialog: {
        open: false,
        variant: 'delete',
        title: '',
        message: '',
        loading: false,
        action: null,
        payload: null,
      },
    };
  },
  computed: {
    ...mapGetters('informationManager', {
      isLoading: 'isLoading',
      requestById: 'requestById',
      infoOwnerList: 'infoOwnerList',
      storeFilteredLocationList: 'filteredLocationList',
      storeLocationList: 'locationList',
      projectMetadata: 'projectMetadata',
      requestList: 'requestList',
    }),
    request() {
      return this.requestById(this.requestId);
    },
    enrichedRequest() {
      if (!this.request) return null;
      const [enriched] = enrichRequestList([JSON.parse(JSON.stringify(this.request))]);
      return enriched;
    },
    requestSerialNumber() {
      const list = this.requestList || [];
      const index = list.findIndex((item) => item._id === this.requestId);
      const fallback = index >= 0 ? index + 1 : null;
      return resolveRequestSerialNumber(this.request || this.enrichedRequest, fallback);
    },
    pbcProjectId() {
      return this.projectMetadata?.pbcproject || '';
    },
    locationList() {
      return this.storeFilteredLocationList?.length
        ? this.storeFilteredLocationList
        : (this.storeLocationList || []);
    },
    locationName() {
      return resolveLocationName(this.enrichedRequest?.location, this.locationList);
    },
    canEdit() {
      return this.$nova.hasRight(this.right, 'edit') || this.$nova.hasRight(this.right, 'add');
    },
    canDelete() {
      return this.$nova.hasRight(this.right, 'delete');
    },
    canComment() {
      return this.canEdit || this.canDelete;
    },
    canSubmitOnBehalf() {
      return this.canEdit && this.enrichedRequest?.stage === 0;
    },
    actionContext() {
      return {
        surface: 'detail',
        canEdit: this.canEdit,
        canDelete: this.canDelete,
      };
    },
    primaryActions() {
      if (!this.enrichedRequest) return [];
      return getRequestActions(this.enrichedRequest, this.actionContext).primary;
    },
    overflowActions() {
      if (!this.enrichedRequest) return [];
      return getRequestActions(this.enrichedRequest, this.actionContext).overflow;
    },
    categoryList() {
      const categories = ['Others'];
      (this.requestList || []).forEach((req) => {
        if (req.category && req.category !== '' && !categories.includes(req.category)) {
          categories.push(req.category);
        }
      });
      return categories;
    },
    formattedRefList() {
      const map = new Map();
      (this.requestList || []).forEach((req) => {
        (req.referenceChecklist || []).forEach((cl) => {
          if (cl._id && !map.has(cl._id)) {
            map.set(cl._id, {
              value: cl._id,
              name: cl.description,
              master_id: cl.master_id,
              ref: cl.ref,
              location: cl.location,
            });
          }
        });
      });
      return Array.from(map.values());
    },
  },
  async created() {
    await this.bootstrap();
  },
  methods: {
    async bootstrap() {
      const projectId = this.cycleId;
      if (!projectId) return;

      if (!this.$store.getters['informationManager/projectId']) {
        await this.$store.dispatch('informationManager/fetchProjectContext', { projectId });
      }

      if (!this.request) {
        await this.$store.dispatch('informationManager/fetchRequests');
      }
    },
    navigateBack() {
      const saved = this.$store.getters['informationManager/getListViewState'](this.cycleId);
      const query = {};
      const cid = this.$route.query.cid || this.$store.state.client;
      if (cid) {
        query.cid = cid;
      }
      if (saved?.activeTab) {
        query.tab = saved.activeTab;
      } else if (this.$route.query.tab) {
        query.tab = this.$route.query.tab;
      }
      this.$router.push({
        path: `/financialclose/project/informationrequests/${this.cycleId}`,
        query,
      });
    },
    onHeaderAction({ actionId }) {
      this.overflowOpen = false;
      switch (actionId) {
        case REQUEST_ACTION_IDS.EDIT_REQUEST:
          this.openEditRequestModal();
          break;
        case REQUEST_ACTION_IDS.APPROVE:
          this.changeRequestStage(2);
          break;
        case REQUEST_ACTION_IDS.REJECT:
          this.changeRequestStage(0);
          break;
        case REQUEST_ACTION_IDS.CANCEL:
          this.openConfirm('cancel', this.requestId);
          break;
        case REQUEST_ACTION_IDS.DELETE:
          this.openConfirm('delete', this.requestId);
          break;
        case REQUEST_ACTION_IDS.RESTORE:
          this.changeRequestStage(0);
          break;
        case REQUEST_ACTION_IDS.DOWNLOAD:
        case REQUEST_ACTION_IDS.DOWNLOAD_ALL:
          this.onDownloadAll();
          break;
        default:
          break;
      }
    },
    openEditRequestModal() {
      this.editingRequest = { ...this.enrichedRequest };
      this.requestFormErrors = {};
      this.requestModalOpen = true;
    },
    clearRequestFormError(field) {
      if (this.requestFormErrors[field]) {
        this.$set(this.requestFormErrors, field, '');
      }
    },
    async onSaveRequest({ mode, id, data }) {
      if (mode !== 'edit') return;

      const requestData = { ...data, _id: id };
      const { errors, hasError } = validateInformationRequest(requestData);
      this.requestFormErrors = errors;
      if (hasError) {
        this.$store.commit('sbError', 'Please fill all required fields correctly.', { root: true });
        return;
      }

      this.requestModalLoading = true;
      try {
        const payload = buildRequestUpdatePayload(requestData);
        const response = await this.$store.dispatch('informationManager/editRequest', {
          requestId: payload._id,
          data: payload,
        });
        if (response?.status === 'success') {
          this.requestModalOpen = false;
          this.editingRequest = null;
        } else if (response?.data?.[0]?.index0) {
          this.requestFormErrors = response.data[0].index0;
        }
      } finally {
        this.requestModalLoading = false;
      }
    },
    async onSaveOwner({ mode, id, data }) {
      this.ownerModalLoading = true;
      try {
        if (mode === 'edit') {
          const response = await this.$store.dispatch('informationManager/editInfoOwner', { id, data, pbcProjectId: this.pbcProjectId });
          if (response?.status === 'success') {
            this.ownerModalOpen = false;
            this.editingOwner = null;
          }
        }
      } finally {
        this.ownerModalLoading = false;
      }
    },
    openConfirm(variant, payload) {
      this.confirmDialog = {
        open: true,
        variant,
        title: '',
        message: '',
        loading: false,
        action: variant,
        payload,
      };
    },
    async onConfirmAction() {
      const { action, payload } = this.confirmDialog;
      this.confirmDialog.loading = true;
      try {
        if (action === 'cancel') {
          await this.changeRequestStage(-1);
        } else if (action === 'delete') {
          await this.$store.dispatch('informationManager/deleteRequest', {
            requestId: payload,
            cycleId: this.cycleId,
          });
          this.navigateBack();
        }
      } finally {
        this.confirmDialog.loading = false;
        this.confirmDialog.open = false;
      }
    },
    async changeRequestStage(stage) {
      await this.$store.dispatch('informationManager/changeRequestStage', {
        requestId: this.requestId,
        stage,
      });
    },
    async onSubmitResponse({ data, files }, done) {
      this.responseLoading = true;
      try {
        await this.$store.dispatch('informationManager/updateResponse', {
          requestId: this.requestId,
          data,
          files,
          client: this.$store.state.client,
        });
        if (typeof done === 'function') done(true);
      } catch (error) {
        if (typeof done === 'function') done(false);
      } finally {
        this.responseLoading = false;
      }
    },
    onDownloadFile(file) {
      downloadAttachment(this.$nova, file);
    },
    onDownloadAll() {
      downloadAllAttachments(this.$nova, this.enrichedRequest);
    },
  },
};
</script>
